import React, { useState, useEffect } from 'react';
import workOrderService from '../api/workOrderService';
import projectService from '../api/projectService';
import userService from '../api/userService';
import Modal from '../components/Modal';
import WorkOrderForm from '../components/WorkOrderForm';
import Button from '../components/Button';
import RowActions from '../components/RowActions';
import { Eye, Pencil } from 'lucide-react';
import { calcularTiempoTranscurrido } from '../utils/tiempoTranscurrido';
// Escala semántica de estado — fuente única de verdad (ver src/theme/status.js)
import { ORDER_STATUS_TONE, getStatusBadgeStyle as getBadgeStyleForTone } from '../theme/status';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08)
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { formatNameWithRole } from '../utils/roles';

// Etiquetas legibles para estados de orden de trabajo
const STATUS_LABELS = {
  PENDING: 'Pendiente',
  ASSIGNED: 'Asignada',
  IN_PROGRESS: 'En Progreso',
  COMPLETED: 'Completada',
  CANCELED: 'Cancelada',
};

// Etiquetas legibles para prioridades — incluye el plazo de atención esperado
const PRIORITY_LABELS = {
  LOW: 'Baja (30 días)',
  MEDIUM: 'Media (15 días)',
  HIGH: 'Alta (8 días)',
  URGENT: 'Urgente (1 día)',
};

/**
 * @description Página de gestión de Órdenes de Trabajo.
 * Permite ver, crear, editar y asignar técnicos.
 * Incluye búsqueda por texto y filtros por estado, prioridad y técnico asignado.
 * @returns {JSX.Element}
 */
const WorkOrders = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error de carga inicial — bloquea el render de la tabla
  const [loadError, setLoadError] = useState('');
  // Error de operación (guardar) — se muestra encima de la tabla sin bloquear
  const [actionError, setActionError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  // Estado del modal de detalle — independiente del modal de edición
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Estado de búsqueda y filtros
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  // Paginación
  const LIMIT = 20;
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [ordersRes, projectsRes, techsRes] = await Promise.all([
          workOrderService.getWorkOrders({ page: 1, limit: LIMIT }),
          projectService.getProjectsLookup(),
          userService.getTechniciansLookup(),
        ]);
        setWorkOrders(ordersRes.data.data);
        setMeta(ordersRes.data.meta);
        setProjects(projectsRes.data.data);
        setTechnicians(techsRes.data.data);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setLoadError('No se pudo cargar los datos. Intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  /**
   * @description Muestra un error de operación y lo limpia automáticamente tras 6 segundos.
   * @param {string} message - Mensaje de error a mostrar.
   */
  const showActionError = (message) => {
    setActionError(message);
    setTimeout(() => setActionError(''), 6000);
  };

  /**
   * @description Recarga solo la lista de órdenes de trabajo desde la API.
   */
  const fetchWorkOrders = async (targetPage = 1) => {
    try {
      const response = await workOrderService.getWorkOrders({ page: targetPage, limit: LIMIT });
      setWorkOrders(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Error al recargar órdenes de trabajo:', error);
      showActionError('No se pudo actualizar la lista de órdenes. Intente nuevamente.');
    }
  };

  /**
   * @description Maneja el guardado (creación o actualización) de una orden de trabajo.
   * @param {object} orderData Datos del formulario.
   */
  const handleSave = async (orderData) => {
    // Eliminar el campo de técnico si no fue seleccionado para no enviar vacío al backend
    const payload = { ...orderData };
    if (!payload.assignedTechnicianId) {
      delete payload.assignedTechnicianId;
    }

    try {
      if (currentOrder) {
        await workOrderService.updateWorkOrder(currentOrder.id, payload);
      } else {
        await workOrderService.createWorkOrder(payload);
      }
      fetchWorkOrders();
      closeModal();
    } catch (error) {
      console.error('Error al guardar orden de trabajo:', error);
      showActionError(error.response?.data?.message || 'No se pudo guardar la orden de trabajo.');
    }
  };

  const openModal = (order = null) => {
    setCurrentOrder(order);
    setShowModal(true);
  };

  const closeModal = () => {
    setCurrentOrder(null);
    setShowModal(false);
  };

  /**
   * @description Abre el modal de detalle y carga los datos completos de la OT desde la API.
   * La carga es lazy: solo se dispara cuando el usuario hace clic en "Ver detalle".
   * @param {string} orderId ID de la orden de trabajo a consultar.
   */
  const openDetailModal = async (orderId) => {
    setDetailOrder(null);
    setDetailError('');
    setDetailLoading(true);
    setShowDetailModal(true);
    try {
      const response = await workOrderService.getWorkOrder(orderId);
      setDetailOrder(response.data);
    } catch (error) {
      console.error('Error al cargar detalle de la orden:', error);
      setDetailError('No se pudo cargar el detalle de la orden. Intente nuevamente.');
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * @description Cierra el modal de detalle y limpia el estado asociado.
   */
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailOrder(null);
    setDetailError('');
  };

  // Filtrado en memoria: combina búsqueda por texto + estado + prioridad + técnico
  const filteredOrders = workOrders.filter((order) => {
    const matchesStatus = statusFilter ? order.status === statusFilter : true;
    const matchesPriority = priorityFilter ? order.priority === priorityFilter : true;
    const matchesProject = projectFilter
      ? String(order.project?.id ?? order.project?.project_id) === projectFilter
      : true;

    // Filtro por técnico: 'unassigned' es opción especial para sin asignar
    const matchesTechnician =
      technicianFilter === ''
        ? true
        : technicianFilter === 'unassigned'
        ? !order.assignedTechnician
        : String(order.assignedTechnician?.id) === technicianFilter ||
          String(order.assignedTechnician?.user_id) === technicianFilter;

    const query = searchText.toLowerCase();
    const matchesSearch =
      query === '' ||
      order.orderNumber?.toLowerCase().includes(query) ||
      order.title?.toLowerCase().includes(query) ||
      order.project?.name?.toLowerCase().includes(query) ||
      order.assignedTechnician?.name?.toLowerCase().includes(query);

    return matchesStatus && matchesPriority && matchesTechnician && matchesProject && matchesSearch;
  });

  if (loading) return <LoadingState message="Cargando datos..." />;

  // Error de carga inicial: no tiene sentido mostrar la tabla sin datos
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div>
      <h2>Órdenes de Trabajo</h2>

      {/* Alerta de error de operación: visible sobre la tabla, se cierra manualmente o tras 6s */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span>{actionError}</span>
          <button
            onClick={() => setActionError('')}
            style={styles.actionErrorClose}
            aria-label="Cerrar mensaje de error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Barra de acciones: búsqueda + filtros + botón crear */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Buscar por título, proyecto o técnico..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todas las prioridades</option>
          {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todos los proyectos</option>
          {projects.map((p) => (
            <option key={p.project_id} value={String(p.project_id)}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={technicianFilter}
          onChange={(e) => setTechnicianFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todos los técnicos</option>
          <option value="unassigned">Sin asignar</option>
          {technicians.map((t) => (
            <option key={t.user_id} value={String(t.user_id)}>
              {t.username}
            </option>
          ))}
        </select>
        {/* Acción primaria de la página: relleno sólido en teal de marca */}
        <Button variant="primary" onClick={() => openModal()} style={{ marginLeft: 'auto' }}>
          Crear Nueva Orden
        </Button>
      </div>

      {/* Contador de resultados visibles */}
      <p style={styles.resultCount}>
        {filteredOrders.length} {filteredOrders.length === 1 ? 'orden encontrada' : 'órdenes encontradas'}
      </p>

      {/* Estado de lista vacía */}
      {filteredOrders.length === 0 ? (
        <p style={styles.emptyMessage}>
          {searchText || statusFilter || priorityFilter || technicianFilter || projectFilter
            ? 'No se encontraron órdenes con los filtros aplicados.'
            : 'No hay órdenes de trabajo registradas.'}
        </p>
      ) : (
        <div className="table-container">
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th} className="col-hide-mobile">#</th>
              <th style={styles.th} className="col-hide-mobile">N° Orden</th>
              <th style={styles.th}>Título</th>
              <th style={styles.th} className="col-hide-mobile">Proyecto</th>
              <th style={styles.th} className="col-hide-mobile">Técnico Asignado</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th} className="col-hide-mobile">Duración</th>
              <th style={styles.th} className="col-hide-mobile">Prioridad</th>
              <th style={styles.th} className="col-actions-sticky">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.id} style={styles.row}>
                <td style={{ ...styles.td, color: 'rgba(56,62,65,0.45)', fontSize: '0.85rem', whiteSpace: 'nowrap' }} className="col-hide-mobile">#{order.id}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'nowrap' }} className="col-hide-mobile">
                  {order.orderNumber || <span style={{ color: '#bbb' }}>—</span>}
                </td>
                <td style={styles.td}>{order.title}</td>
                <td style={styles.td} className="col-hide-mobile">{order.project?.name || '—'}</td>
                <td style={styles.td} className="col-hide-mobile">
                  {order.assignedTechnician?.name ||
                    <span style={styles.unassigned}>No asignado</span>}
                </td>
                <td style={styles.td}>
                  <span style={getStatusBadgeStyle(order.status)}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }} className="col-hide-mobile">
                  {(() => {
                    const { duracion, estaEnCurso } = calcularTiempoTranscurrido(
                      order.createdAt,
                      order.closedAt,
                      order.updatedAt,
                      order.status,
                    );
                    return (
                      <span style={getDuracionStyle(estaEnCurso)}>
                        {duracion}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }} className="col-hide-mobile">
                  <span style={getPriorityBadgeStyle(order.priority)}>
                    {PRIORITY_LABELS[order.priority] || order.priority || '—'}
                  </span>
                </td>
                <td style={styles.td} className="col-actions-sticky">
                  {/* Ninguna de las dos es destructiva: ambas comparten el mismo
                      tratamiento visual "acción esperada" (tinte teal) — ver RowActions.js */}
                  <RowActions
                    actions={[
                      { key: 'detail', label: 'Ver detalle', icon: Eye, onClick: () => openDetailModal(order.id) },
                      { key: 'edit', label: 'Editar / Asignar', icon: Pencil, onClick: () => openModal(order) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Controles de paginación */}
      {meta.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => fetchWorkOrders(meta.page - 1)}
            disabled={meta.page <= 1}
            style={{ ...styles.pageButton, opacity: meta.page <= 1 ? 0.4 : 1 }}
          >
            ← Anterior
          </button>
          <span style={styles.pageInfo}>
            Página {meta.page} de {meta.totalPages} &middot; {meta.total} resultado{meta.total !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => fetchWorkOrders(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            style={{ ...styles.pageButton, opacity: meta.page >= meta.totalPages ? 0.4 : 1 }}
          >
            Siguiente →
          </button>
        </div>
      )}

      <Modal
        show={showModal}
        onClose={closeModal}
        title={currentOrder ? 'Editar Orden' : 'Crear Orden'}
      >
        <WorkOrderForm
          workOrder={currentOrder}
          projects={projects}
          technicians={technicians}
          onSave={handleSave}
          onCancel={closeModal}
        />
      </Modal>

      {/* Modal de detalle: más ancho que el modal estándar para mostrar formularios y adjuntos */}
      <Modal
        show={showDetailModal}
        onClose={closeDetailModal}
        title="Detalle de Orden de Trabajo"
        modalStyle={detailStyles.modalOverride}
      >
        {detailLoading && (
          <LoadingState message="Cargando detalle..." compact />
        )}

        {detailError && (
          <p style={detailStyles.errorText}>{detailError}</p>
        )}

        {!detailLoading && !detailError && detailOrder && (
          <WorkOrderDetail order={detailOrder} />
        )}
      </Modal>
    </div>
  );
};

/**
 * @description Genera el estilo del badge de estado de la orden de trabajo.
 * Delega en la escala semántica compartida (theme/status.js) para que el mismo
 * estado se pinte igual en Dashboard, WorkOrders y MaintenanceOrders.
 * @param {string} status - Valor del estado (PENDING, ASSIGNED, etc.).
 * @returns {object} Objeto de estilos inline.
 */
const getStatusBadgeStyle = (status) => getBadgeStyleForTone(ORDER_STATUS_TONE[status]);

/**
 * @description Genera el estilo del texto de duración en la tabla.
 * Las OTs activas se muestran con color ámbar para indicar que el tiempo corre.
 * Las OTs cerradas se muestran en gris neutro.
 * @param {boolean} estaEnCurso - True si la OT aún no ha sido cerrada.
 * @returns {object} Objeto de estilos inline.
 */
const getDuracionStyle = (estaEnCurso) => ({
  fontSize: '0.82rem',
  fontWeight: '500',
  color: estaEnCurso ? '#856404' : '#555',
});

/**
 * @description Genera el estilo del badge de prioridad de la orden de trabajo.
 * @param {string} priority - Valor de la prioridad (LOW, MEDIUM, HIGH).
 * @returns {object} Objeto de estilos inline.
 */
const getPriorityBadgeStyle = (priority) => {
  const colors = {
    LOW: { backgroundColor: '#f0f9f0', color: '#2e7d32', border: '1px solid #a5d6a7' },
    MEDIUM: { backgroundColor: '#fef9e7', color: '#856404', border: '1px solid #ffc107' },
    HIGH: { backgroundColor: '#fdecea', color: '#c0392b', border: '1px solid #f5c6cb' },
    URGENT: { backgroundColor: '#4a0000', color: '#ffffff', border: '1px solid #c0392b' },
  };
  return {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    ...(colors[priority] || { backgroundColor: '#f0f0f0', color: '#555' }),
  };
};

/**
 * @description Componente interno que renderiza el contenido del modal de detalle de una OT.
 * Muestra información general, técnico asignado, creador y la lista de formularios de evidencia.
 * @param {object} props
 * @param {object} props.order Datos completos de la OT obtenidos de GET /work-orders/:id
 * @returns {JSX.Element}
 */
const WorkOrderDetail = ({ order }) => {
  /**
   * @description Formatea una fecha ISO a una cadena legible en español (es-CL).
   * @param {string | null} dateString Fecha en formato ISO.
   * @returns {string} Fecha formateada o '—' si no existe.
   */
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calcular tiempo transcurrido para el panel de detalle
  const { duracion, estaEnCurso, etiqueta } = calcularTiempoTranscurrido(
    order.createdAt,
    order.closedAt,
    order.updatedAt,
    order.status,
  );

  return (
    <div>
      {/* Sección: Información general */}
      <section style={detailStyles.section}>
        <h3 style={detailStyles.sectionTitle}>Información General</h3>
        <div style={detailStyles.infoGrid}>
          {order.orderNumber && (
            <div style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>N° de Orden</span>
              <span style={{ ...detailStyles.infoValue, fontFamily: 'monospace', fontWeight: 'bold' }}>{order.orderNumber}</span>
            </div>
          )}
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Título</span>
            <span style={detailStyles.infoValue}>{order.title}</span>
          </div>
          {order.description && (
            <div style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>Descripción</span>
              <span style={detailStyles.infoValue}>{order.description}</span>
            </div>
          )}
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Estado</span>
            <span>
              <span style={getStatusBadgeStyle(order.status)}>
                {STATUS_LABELS[order.status] || order.status}
              </span>
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Prioridad</span>
            <span>
              <span style={getPriorityBadgeStyle(order.priority)}>
                {PRIORITY_LABELS[order.priority] || order.priority || '—'}
              </span>
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Proyecto</span>
            <span style={detailStyles.infoValue}>
              {order.project?.name || '—'}
              {order.project?.address && (
                <span style={detailStyles.subInfo}> · {order.project.address}</span>
              )}
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Técnico asignado</span>
            <span style={detailStyles.infoValue}>
              {order.assignedTechnician
                ? order.assignedTechnician.name
                : <span style={detailStyles.unassignedText}>Sin asignar</span>}
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Creado por</span>
            <span style={detailStyles.infoValue}>{order.creator?.name || '—'}</span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Creado el</span>
            <span style={detailStyles.infoValue}>{formatDate(order.createdAt)}</span>
          </div>
          {/* Fila de tiempo transcurrido: siempre visible si hay fecha de creación */}
          {order.createdAt && (
            <div style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>{etiqueta || 'Duración'}</span>
              <span style={{
                ...detailStyles.infoValue,
                fontWeight: '600',
                color: estaEnCurso ? '#856404' : '#2e7d32',
              }}>
                {duracion}
                {estaEnCurso && (
                  <span style={{ fontWeight: 'normal', color: '#aaa', fontSize: '0.8rem', marginLeft: '6px' }}>
                    (en curso)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Separador visual */}
      <hr style={detailStyles.divider} />

      {/* Sección: Formularios de evidencia */}
      <section style={detailStyles.section}>
        <h3 style={detailStyles.sectionTitle}>
          Formularios de Evidencia
          <span style={detailStyles.formCount}>{order.forms?.length || 0}</span>
        </h3>

        {!order.forms || order.forms.length === 0 ? (
          <p style={detailStyles.emptyForms}>
            El técnico aún no ha enviado formularios de evidencia para esta orden.
          </p>
        ) : (
          <div style={detailStyles.formsList}>
            {order.forms.map((form) => (
              <div key={form.work_order_form_id} style={detailStyles.formCard}>
                {/* Cabecera del formulario: técnico + fecha */}
                <div style={detailStyles.formHeader}>
                  <span style={detailStyles.formAuthor}>
                    {/* Rol con el que registró la evidencia, no su rol actual */}
                    {form.author?.name
                      ? formatNameWithRole(form.author.name, form.author.role)
                      : 'Técnico desconocido'}
                  </span>
                  <span style={detailStyles.formDate}>{formatDate(form.createdAt)}</span>
                </div>

                {/* Nota del técnico */}
                {form.note && (
                  <p style={detailStyles.formNote}>{form.note}</p>
                )}

                {/* Adjuntos: thumbnails clicables que abren la imagen en nueva pestaña */}
                {form.attachments && form.attachments.length > 0 && (
                  <div style={detailStyles.attachmentsGrid}>
                    {form.attachments.map((attachment) => (
                      <a
                        key={attachment.form_attachment_id}
                        href={attachment.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={detailStyles.thumbnailLink}
                        title="Ver imagen en tamaño completo"
                      >
                        <img
                          src={attachment.file_url}
                          alt="Evidencia adjunta"
                          style={detailStyles.thumbnail}
                          onError={(e) => {
                            // Si la imagen no carga, mostrar placeholder con tipo de archivo
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {/* Fallback visible solo si la imagen falla */}
                        <div style={{ ...detailStyles.thumbnailFallback, display: 'none' }}>
                          <span style={detailStyles.thumbnailFallbackText}>
                            {attachment.file_type || 'Archivo'}
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {/* Formulario sin nota ni adjuntos — caso borde */}
                {!form.note && (!form.attachments || form.attachments.length === 0) && (
                  <p style={detailStyles.emptyForms}>Sin contenido registrado.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// Estilos exclusivos del modal de detalle
const detailStyles = {
  // Sobreescribe el width: 500px del componente Modal base via prop modalStyle
  modalOverride: {
    width: '780px',
  },
  // loading ahora vive en LoadingState (compact), ver hallazgo de auditoría #08
  errorText: {
    color: '#c0392b',
    textAlign: 'center',
    padding: '20px 0',
  },
  section: {
    marginBottom: '8px',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 'bold',
    color: '#444',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  formCount: {
    backgroundColor: '#e8f4fd',
    color: '#1a6496',
    border: '1px solid #bce0fd',
    borderRadius: '10px',
    padding: '1px 8px',
    fontSize: '0.78rem',
    fontWeight: 'bold',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #eee',
    margin: '16px 0',
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  infoRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'baseline',
  },
  infoLabel: {
    minWidth: '150px',
    fontSize: '0.85rem',
    color: '#888',
    flexShrink: 0,
  },
  infoValue: {
    fontSize: '0.9rem',
    color: '#333',
  },
  subInfo: {
    color: '#888',
    fontSize: '0.85rem',
  },
  unassignedText: {
    color: '#999',
    fontStyle: 'italic',
    fontSize: '0.9rem',
  },
  formsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '360px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  formCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '12px 14px',
    backgroundColor: '#fafafa',
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  formAuthor: {
    fontWeight: 'bold',
    fontSize: '0.88rem',
    color: '#333',
  },
  formDate: {
    fontSize: '0.78rem',
    color: '#999',
  },
  formNote: {
    fontSize: '0.88rem',
    color: '#555',
    marginBottom: '10px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  attachmentsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  thumbnailLink: {
    display: 'block',
    borderRadius: '4px',
    overflow: 'hidden',
    border: '1px solid #ddd',
    width: '80px',
    height: '80px',
    cursor: 'pointer',
    flexShrink: 0,
    position: 'relative',
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    display: 'block',
  },
  thumbnailFallback: {
    width: '80px',
    height: '80px',
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  thumbnailFallbackText: {
    fontSize: '0.65rem',
    color: '#777',
    textAlign: 'center',
    padding: '4px',
    wordBreak: 'break-all',
  },
  emptyForms: {
    color: '#999',
    fontStyle: 'italic',
    fontSize: '0.88rem',
    padding: '8px 0',
  },
};

// Estilos de la página, consistentes con Users.js y Projects.js
const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  searchInput: {
    padding: '8px',
    minWidth: '220px',
    flex: '1',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  filterSelect: {
    padding: '8px',
    minWidth: '160px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  resultCount: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '8px',
  },
  // Densidad, zebra, hover y columna de acciones fija ahora viven en
  // responsive.css (.table-container table ...) — ver hallazgo de auditoría #04.
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {},
  th: {},
  row: {},
  td: {},
  unassigned: {
    color: '#999',
    fontStyle: 'italic',
  },
  emptyMessage: {
    color: '#777',
    fontStyle: 'italic',
  },
  // Banner de error para operaciones fallidas (guardar)
  actionErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fdecea',
    color: '#c0392b',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '0.9rem',
  },
  actionErrorClose: {
    background: 'none',
    border: 'none',
    color: '#c0392b',
    cursor: 'pointer',
    fontSize: '1.2rem',
    lineHeight: '1',
    padding: '0 0 0 12px',
    fontWeight: 'bold',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 0',
    marginTop: '8px',
  },
  pageButton: {
    padding: '6px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#374151',
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
};

export default WorkOrders;
